import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Check,
  Key,
  LayoutDashboard,
  Package,
  SkipForward,
} from 'lucide-react';
import { api } from '../../api/client';
import { getProductCatalog } from '../../api/productCatalog';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { BrandMark } from '../../components/brand/BrandWordmark';
import { localizeKnownCopy } from '../../i18n/content';
import {
  PRODUCT_PRESENTATIONS,
  ProductConceptArtwork,
} from '../../components/public/ProductConceptArtwork';
import type { Language, Product, Theme, UserExperience } from '../../types';

type Step = 'welcome' | 'ecosystem' | 'start' | 'personalize' | 'ready';
type ExperiencePatch = Partial<UserExperience> & {
  name?: string;
  complete?: boolean;
  skip?: boolean;
};

const STEPS: Step[] = ['welcome', 'ecosystem', 'start', 'personalize', 'ready'];

const START_OPTIONS = [
  { id: 'products', path: '/applications', icon: Package, labelKey: 'welcome.start.products' },
  { id: 'portal', path: '/customer', icon: LayoutDashboard, labelKey: 'welcome.start.portal' },
  { id: 'licenses', path: '/customer/licenses', icon: Key, labelKey: 'welcome.start.licenses' },
  { id: 'docs', path: '/docs', icon: BookOpen, labelKey: 'welcome.start.docs' },
] as const;

function stepIndex(step: Step) {
  return STEPS.indexOf(step);
}

export const WelcomeExperience: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, markOnboardingComplete } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();

  const [step, setStep] = useState<Step>('welcome');
  const [busy, setBusy] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [notifications, setNotifications] = useState({
    productUpdates: true,
    security: true,
    announcements: true,
  });
  const [preferredProduct, setPreferredProduct] = useState<string | undefined>();
  const [landingPath, setLandingPath] = useState('/customer');
  const [ready, setReady] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getProductCatalog()
      .then((catalog) => {
        if (!cancelled) setProducts(catalog);
      })
      .catch(() => {
        if (!cancelled) setCatalogError(true);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .getExperience()
      .then((state) => {
        if (cancelled) return;
        if (!state.needsOnboarding) {
          navigate(state.experience.lastLandingPath || '/customer', { replace: true });
          return;
        }
        const s = state.experience.onboardingStep;
        if (s && s !== 'done' && STEPS.includes(s as Step)) setStep(s as Step);
        if (state.profile.name) setDisplayName(state.profile.name);
        if (state.experience.timezone) setTimezone(state.experience.timezone);
        if (state.experience.notifications) {
          setNotifications({
            productUpdates: state.experience.notifications.productUpdates !== false,
            security: state.experience.notifications.security !== false,
            announcements: state.experience.notifications.announcements !== false,
          });
        }
        if (state.experience.preferredProduct) setPreferredProduct(state.experience.preferredProduct);
        if (state.experience.lastLandingPath) setLandingPath(state.experience.lastLandingPath);
        if (state.experience.language) setLanguage(state.experience.language);
        if (state.experience.theme) setTheme(state.experience.theme);
      })
      .catch(() => {
        /* allow local flow */
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, setLanguage, setTheme]);

  const persist = useCallback(async (patch: ExperiencePatch) => {
    await api.patchExperience(patch);
  }, []);

  const go = useCallback(
    async (next: Step, patch: ExperiencePatch = {}) => {
      setSaveError(false);
      setStep(next);
      try {
        await persist({
          name: displayName.trim() || undefined,
          timezone,
          notifications,
          preferredProduct,
          lastLandingPath: landingPath,
          language,
          theme,
          ...patch,
          onboardingStep: next,
        });
      } catch {
        setSaveError(true);
      }
    },
    [
      displayName,
      landingPath,
      language,
      notifications,
      persist,
      preferredProduct,
      theme,
      timezone,
    ]
  );

  const finish = useCallback(
    async (opts?: { skip?: boolean; path?: string }) => {
      setBusy(true);
      setSaveError(false);
      const path = opts?.path || landingPath || '/customer';
      try {
        await persist({
          skip: opts?.skip,
          complete: !opts?.skip,
          onboardingStep: 'done',
          name: displayName.trim() || undefined,
          timezone,
          notifications,
          preferredProduct,
          lastLandingPath: path,
          language,
          theme,
        });
        markOnboardingComplete();
        setReady(true);
        window.setTimeout(() => navigate(path, { replace: true }), 900);
      } catch {
        setSaveError(true);
      } finally {
        setBusy(false);
      }
    },
    [
      displayName,
      landingPath,
      language,
      markOnboardingComplete,
      navigate,
      notifications,
      persist,
      preferredProduct,
      theme,
      timezone,
    ]
  );

  const progress = useMemo(() => ((stepIndex(step) + 1) / STEPS.length) * 100, [step]);

  if (ready) {
    return (
      <div className="bc-welcome">
        <div className="bc-welcome-atmosphere" aria-hidden="true" />
        <div className="bc-welcome-shell bc-welcome-ready">
          <BrandMark className="h-10 w-10" />
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-paper">
            {t('welcome.ready.title')}
          </h1>
          <p className="text-sm text-zinc-400 max-w-md">{t('welcome.ready.body')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bc-welcome">
      <div className="bc-welcome-atmosphere" aria-hidden="true" />
      <div className="bc-welcome-shell">
        <header className="bc-welcome-top">
          <div className="flex items-center gap-3">
            <BrandMark className="h-7 w-7" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-400">
              BeCoder
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="bc-welcome-progress"
              role="progressbar"
              aria-label={t('welcome.progress', {
                current: stepIndex(step) + 1,
                total: STEPS.length,
              })}
              aria-valuemin={1}
              aria-valuemax={STEPS.length}
              aria-valuenow={stepIndex(step) + 1}
            >
              <span style={{ width: `${progress}%` }} />
            </div>
            <button
              type="button"
              className="bc-welcome-skip"
              disabled={busy}
              onClick={() => void finish({ skip: true, path: '/customer' })}
            >
              <SkipForward className="w-3.5 h-3.5" />
              {t('welcome.skip')}
            </button>
          </div>
        </header>

        <div className="bc-welcome-stage" key={step}>
          {step === 'welcome' && (
            <section className="bc-welcome-panel space-y-5 max-w-xl mx-auto text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-400">
                {t('welcome.step1.eyebrow')}
              </p>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-paper leading-[1.05]">
                {t('welcome.step1.title')}
              </h1>
              <p className="text-base text-zinc-400 leading-relaxed">{t('welcome.step1.body')}</p>
              <button type="button" className="bc-btn-primary !px-6 !py-3 mx-auto" onClick={() => void go('ecosystem')}>
                {t('welcome.continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </section>
          )}

          {step === 'ecosystem' && (
            <section className="bc-welcome-panel space-y-6">
              <div className="text-center space-y-2 max-w-xl mx-auto">
                <h2 className="font-display text-3xl font-bold text-paper tracking-tight">
                  {t('welcome.step2.title')}
                </h2>
                <p className="text-sm text-zinc-400">{t('welcome.step2.body')}</p>
              </div>
              {catalogLoading && (
                <div className="grid sm:grid-cols-2 gap-3.5" role="status" aria-label={t('common.loading')}>
                  {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="bc-welcome-product bc-product-skeleton" aria-hidden />
                  ))}
                </div>
              )}
              {!catalogLoading && catalogError && (
                <div className="bc-welcome-catalog-note" role="alert">
                  <p>{t('welcome.catalogError')}</p>
                  <Link to="/applications" className="bc-link">
                    {t('welcome.start.products')}
                  </Link>
                </div>
              )}
              {!catalogLoading && !catalogError && products.length === 0 && (
                <div className="bc-welcome-catalog-note">
                  <p>{t('welcome.catalogEmpty')}</p>
                </div>
              )}
              {!catalogLoading && products.length > 0 && (
                <ul className="grid sm:grid-cols-2 gap-3.5">
                {products.map((product) => {
                  const presentation = PRODUCT_PRESENTATIONS.find(
                    (item) => item.id === product.slug
                  );
                  const selected = preferredProduct === product.slug;
                  return (
                    <li key={product.id}>
                      <article className={`bc-welcome-product ${selected ? 'is-selected' : ''}`}>
                        <button
                          type="button"
                          className="block w-full text-start"
                          aria-pressed={selected}
                          aria-label={t('welcome.selectProduct', { name: product.name })}
                          onClick={() => setPreferredProduct(product.slug)}
                        >
                          <div className="bc-welcome-product-cover">
                            {product.bannerUrl ? (
                              <img
                                src={product.bannerUrl}
                                alt=""
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            ) : presentation ? (
                              <ProductConceptArtwork presentation={presentation} />
                            ) : (
                              <div className="h-full grid place-items-center" aria-hidden>
                                {product.iconUrl ? (
                                  <img
                                    src={product.iconUrl}
                                    alt=""
                                    className="h-14 w-14 object-contain"
                                    loading="lazy"
                                  />
                                ) : (
                                  <Package className="h-10 w-10 text-brand-400" strokeWidth={1.5} />
                                )}
                              </div>
                            )}
                          </div>
                          <div className="space-y-1.5 text-start p-4 pb-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-display text-lg font-bold text-paper">{product.name}</p>
                            <span className="text-[10px] uppercase tracking-wider text-brand-400">
                              {t(`product.status.${product.status}`, product.status)}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-400 leading-relaxed">
                            {localizeKnownCopy(product.tagline || product.descriptionMd, t)}
                          </p>
                          </div>
                        </button>
                        <div className="px-4 pb-4">
                          <Link
                            to={`/applications/${product.slug}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-400"
                          >
                            {t('welcome.learnMore')}
                            <ArrowRight className="w-3 h-3" />
                          </Link>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
              )}
              <div className="flex justify-center gap-3">
                <button type="button" className="bc-welcome-ghost" onClick={() => void go('welcome')}>
                  {t('welcome.back')}
                </button>
                <button
                  type="button"
                  className="bc-btn-primary !px-6 !py-3"
                  onClick={() => void go('start', { preferredProduct })}
                >
                  {t('welcome.continue')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </section>
          )}

          {step === 'start' && (
            <section className="bc-welcome-panel space-y-6 max-w-2xl mx-auto">
              <div className="text-center space-y-2">
                <h2 className="font-display text-3xl font-bold text-paper tracking-tight">
                  {t('welcome.step3.title')}
                </h2>
                <p className="text-sm text-zinc-400">{t('welcome.step3.body')}</p>
              </div>
              <ul className="space-y-2">
                {START_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <li key={opt.id}>
                      <button
                        type="button"
                        className={`bc-welcome-choice ${landingPath === opt.path ? 'is-selected' : ''}`}
                        onClick={() => setLandingPath(opt.path)}
                      >
                        <Icon className="w-4 h-4 text-brand-400 shrink-0" />
                        <span className="flex-1 text-start text-sm font-semibold text-paper">
                          {t(opt.labelKey)}
                        </span>
                        {landingPath === opt.path && <Check className="w-4 h-4 text-brand-400" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="flex justify-center gap-3">
                <button type="button" className="bc-welcome-ghost" onClick={() => void go('ecosystem')}>
                  {t('welcome.back')}
                </button>
                <button
                  type="button"
                  className="bc-btn-primary !px-6 !py-3"
                  onClick={() => void go('personalize', { lastLandingPath: landingPath })}
                >
                  {t('welcome.continue')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </section>
          )}

          {step === 'personalize' && (
            <section className="bc-welcome-panel space-y-6 max-w-lg mx-auto">
              <div className="text-center space-y-2">
                <h2 className="font-display text-3xl font-bold text-paper tracking-tight">
                  {t('welcome.step4.title')}
                </h2>
                <p className="text-sm text-zinc-400">{t('welcome.step4.body')}</p>
              </div>
              <div className="space-y-4 text-start">
                <label className="block space-y-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    {t('welcome.field.name')}
                  </span>
                  <input
                    className="bc-welcome-input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={user?.name || ''}
                  />
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block space-y-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                      {t('welcome.field.language')}
                    </span>
                    <select
                      className="bc-welcome-input"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value as Language)}
                    >
                      <option value="en">English</option>
                      <option value="ar">العربية</option>
                      <option value="ckb">کوردی</option>
                    </select>
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                      {t('welcome.field.theme')}
                    </span>
                    <select
                      className="bc-welcome-input"
                      value={theme}
                      onChange={(e) => setTheme(e.target.value as Theme)}
                    >
                      <option value="dark">{t('welcome.theme.dark')}</option>
                      <option value="light">{t('welcome.theme.light')}</option>
                      <option value="system">{t('welcome.theme.system')}</option>
                    </select>
                  </label>
                </div>
                <label className="block space-y-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    {t('welcome.field.timezone')}
                  </span>
                  <input
                    className="bc-welcome-input"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  />
                </label>
                <fieldset className="space-y-2">
                  <legend className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    {t('welcome.field.notifications')}
                  </legend>
                  {(
                    [
                      ['productUpdates', 'welcome.notify.products'],
                      ['security', 'welcome.notify.security'],
                      ['announcements', 'welcome.notify.announcements'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm text-zinc-300">
                      <input
                        type="checkbox"
                        checked={notifications[key]}
                        onChange={(e) =>
                          setNotifications((prev) => ({ ...prev, [key]: e.target.checked }))
                        }
                      />
                      {t(label)}
                    </label>
                  ))}
                </fieldset>
                {user?.avatarUrl && (
                  <div className="flex items-center gap-3 pt-1">
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover border border-white/10"
                    />
                    <p className="text-xs text-zinc-500">{t('welcome.field.photoHint')}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <button type="button" className="bc-welcome-ghost" onClick={() => void go('start')}>
                  {t('welcome.back')}
                </button>
                <button
                  type="button"
                  className="bc-welcome-ghost"
                  onClick={() => void go('ready')}
                >
                  {t('welcome.skipStep')}
                </button>
                <button
                  type="button"
                  className="bc-btn-primary !px-6 !py-3"
                  onClick={() =>
                    void go('ready', {
                      name: displayName.trim() || undefined,
                      timezone,
                      notifications,
                      language,
                      theme,
                    })
                  }
                >
                  {t('welcome.continue')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </section>
          )}

          {step === 'ready' && (
            <section className="bc-welcome-panel space-y-6 max-w-md mx-auto text-center">
              <div className="bc-welcome-check" aria-hidden>
                <Check className="w-7 h-7" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-paper tracking-tight">
                {t('welcome.step5.title')}
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed">{t('welcome.step5.body')}</p>
              <button
                type="button"
                className="bc-btn-primary !px-6 !py-3 mx-auto"
                disabled={busy}
                onClick={() => void finish()}
              >
                {t('welcome.enter')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </section>
          )}
        </div>

        {saveError && (
          <div className="bc-welcome-save-error" role="alert">
            {t('welcome.saveError')}
          </div>
        )}

        <footer className="bc-welcome-foot">
          <span className="text-[11px] text-zinc-600">
            {t('welcome.progress', { current: stepIndex(step) + 1, total: STEPS.length })}
          </span>
          {user?.email && (
            <button type="button" className="text-[11px] text-zinc-600 hover:text-zinc-400" onClick={() => void logout()}>
              {user.email}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};
